// omakaseDev（社内専用）を有効にする環境の判定。
//
// **`lib/auth0.tsx` はサーバ側で 1 度だけ評価される**ため、`window.location.hostname` を見る
// `getNitoelEnv()`（components/EnvInfo）は使えない。ビルド時に埋め込まれる `NEXT_PUBLIC_KIND` を使う。
//
// 認証まわりの共通判定なので、特定機能（omakase 等）の配下には置かない
// （`lib/auth0.tsx` は全画面が通る。feature スコープへ依存させない）。

/**
 * omakaseDev を有効にする `NEXT_PUBLIC_KIND` の値。
 *
 * <b>ここは env にしない。</b> サーバ（v2）と agent は `OMAKASE_ENABLED_KINDS` で切り替えるが、
 * フロントのゲートは**認可の正本ではなく UX のための出し分け**である（認可は v2 の 4 ゲートが唯一の
 * 防壁）。正本でないものを env で二重管理する利得が無いうえ、`NEXT_PUBLIC_` を増やすと配り忘れた
 * 環境で「アイコンが出ない」という原因の見えない壊れ方をする。
 *
 * 環境を増やすときは、ここと terraform の `OMAKASE_ENABLED_KINDS` の**両方**を更新する。
 */
const OMAKASE_ENABLED_KINDS: readonly string[] = ["dev", "stg"];

/**
 * omakaseDev を出してよい環境か。
 *
 * <p>**未設定は false へ倒す**（fail-closed）。`NEXT_PUBLIC_KIND` の設定漏れがあれば機能が出ず
 * 気づけるが、逆に倒すと設定漏れの環境で社内専用機能が顔を出す。
 *
 * <p>大文字は許さない（env の値は小文字で統一されている）。前後の空白だけ落とす —— tfvars からの
 * 流し込みで混ざり得るため。
 */
export function isOmakaseDevEnabledFor(kind: string | undefined): boolean {
  const normalized = kind?.trim() ?? "";
  return normalized !== "" && OMAKASE_ENABLED_KINDS.includes(normalized);
}

/**
 * Auth0 の `/auth/access-token` エンドポイントを有効にしてよい環境か。
 *
 * <p>nextjs-auth0 v4 の既定は **true**（`enableAccessTokenEndpoint ?? true`）で、prod を含む全環境で
 * この口が開いている。**この口自体を呼んでいる画面コードは無い**（フロントは BFF
 * `app/api/tenant-api-proxy` 経由で API を叩く）ため、閉じても挙動は変わらない。
 *
 * <p><b>誤解しやすい点</b>: これを閉じても「ブラウザがアクセストークンを取得できる経路」が消えるわけではない。
 * 同等の自作エンドポイント `pages/api/get_access_token.tsx` が別に存在し、そちらは残る
 * （管理ユーザーのプロフィール画面の「取得」ボタンが使う。**別対応**）。ここで閉じるのは
 * <b>使われていないのに開いている口</b>だけである。
 *
 * <p>この口を開けておく必要があるのは、ブラウザからおまかせエージェントへ直結するときだけ。
 * したがって **omakaseDev が有効な環境だけ開ける**（判定を 2 つ持つと片方だけ直す事故が起きるため
 * {@link isOmakaseDevEnabledFor} に委ねる）。
 */
export function enableAccessTokenEndpointFor(kind: string | undefined): boolean {
  return isOmakaseDevEnabledFor(kind);
}

/**
 * AI ネイティブ コスト分析（プロトタイプ）を出す `NEXT_PUBLIC_KIND` の値。
 *
 * <p>プロトタイプは main にマージして dev で動かすが、顧客の環境には出さない（PdM 判断 2026-10-22）。
 * omakaseDev と同じく UX の出し分けで、相談の口（v2）はテナントごとの AI の入口の ON/OFF で閉じる。
 */
const COST_ANALYSIS_PROTOTYPE_KINDS: readonly string[] = ["dev"];

/**
 * コスト分析のプロトタイプを出してよいか。
 *
 * <p>本番ビルドでないとき（手元の `next dev`・試験）は環境によらず出す。手元では `NEXT_PUBLIC_KIND` を
 * 入れないため。`NODE_ENV` が無いときは本番ビルドとみなして閉じる側へ倒す。
 */
export function isCostAnalysisPrototypeEnabledFor(kind: string | undefined, nodeEnv: string | undefined): boolean {
  if (nodeEnv !== undefined && nodeEnv !== "production") return true;
  const normalized = kind?.trim() ?? "";
  return normalized !== "" && COST_ANALYSIS_PROTOTYPE_KINDS.includes(normalized);
}
